/**
 * The four plain fields and the footer for the edit-profile form. The
 * username field is separate because it has async behavior. `required` is a
 * prop rather than an asterisk in a label so screen readers get it too.
 * `Save` sits above `Cancel` at 390px per the design frame.
 * `emailSlot` renders between `Phone number` and the footer, which is where
 * the design frame draws the read-only email row.
 */
import { Button, Input } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  BIRTHDAY_HINT,
  BIRTHDAY_LABEL,
  CANCEL_LABEL,
  FIRST_NAME_LABEL,
  LAST_NAME_LABEL,
  PHONE_HINT,
  PHONE_LABEL,
  SAVE_LABEL,
  SAVE_PENDING_LABEL,
  type typeEditProfileErrors,
  type typeEditProfileForm,
} from "./editProfileLogic";

export const EditProfileFields = ({
  form,
  errors,
  isSaving,
  onFieldChange,
  onSave,
  onCancel,
  emailSlot,
}: {
  form: typeEditProfileForm;
  errors: typeEditProfileErrors;
  isSaving: boolean;
  onFieldChange: (field: keyof typeEditProfileForm, next: string) => void;
  onSave: () => void;
  onCancel: () => void;
  emailSlot: ReactNode;
}): ReactNode => {
  return (
    <>
      <Input
        label={FIRST_NAME_LABEL}
        required
        value={form.firstName}
        error={errors.firstName}
        data-testid="edit-profile-first-name"
        autoComplete="given-name"
        onChange={(event) => {
          onFieldChange("firstName", event.target.value);
        }}
      />
      <Input
        label={LAST_NAME_LABEL}
        value={form.lastName}
        error={errors.lastName}
        data-testid="edit-profile-last-name"
        autoComplete="family-name"
        onChange={(event) => {
          onFieldChange("lastName", event.target.value);
        }}
      />
      <Input
        label={BIRTHDAY_LABEL}
        required
        type="date"
        hint={BIRTHDAY_HINT}
        value={form.birthday}
        error={errors.birthday}
        data-testid="edit-profile-birthday"
        onChange={(event) => {
          onFieldChange("birthday", event.target.value);
        }}
      />
      <Input
        label={PHONE_LABEL}
        type="tel"
        inputMode="tel"
        hint={PHONE_HINT}
        value={form.phone}
        error={errors.phone}
        data-testid="edit-profile-phone"
        autoComplete="tel"
        onChange={(event) => {
          onFieldChange("phone", event.target.value);
        }}
      />
      {emailSlot}
      <div className="flex flex-col gap-2 md:flex-row-reverse md:justify-start">
        <Button
          variant="primary"
          fullWidth
          className="md:w-auto"
          loading={isSaving}
          loadingLabel={SAVE_PENDING_LABEL}
          data-testid="edit-profile-save"
          onClick={onSave}
        >
          {SAVE_LABEL}
        </Button>
        <Button
          variant="secondary"
          fullWidth
          className="md:w-auto"
          disabled={isSaving}
          data-testid="edit-profile-cancel"
          onClick={onCancel}
        >
          {CANCEL_LABEL}
        </Button>
      </div>
    </>
  );
};
